import React, { useState, useEffect } from 'react';
import { 
  FileText, 
  CheckCircle, 
  XCircle, 
  Clock, 
  Flag,
  Eye, 
  Search, 
  RefreshCw,
  Download,
  Mail,
  Phone,
  Calendar,
  User,
  Briefcase
} from 'lucide-react';

const JobApplicationsManagement = () => {
  const [applications, setApplications] = useState([]);
  const [stats, setStats] = useState({});
  const [loading, setLoading] = useState(true);
  const [filters, setFilters] = useState({
    status: '',
    position: '',
    search: ''
  });
  const [page, setPage] = useState(1);
  const [pagination, setPagination] = useState({});
  const [selectedApplication, setSelectedApplication] = useState(null);
  const [showModal, setShowModal] = useState(false);
  const [positions, setPositions] = useState([]);

  useEffect(() => {
    fetchApplications();
    fetchPositions();
    fetchStats();
  }, [page, filters]);

  const fetchApplications = async () => {
    setLoading(true);
    try {
      const params = new URLSearchParams({
        page: page.toString(),
        limit: '10',
        ...(filters.status && { status: filters.status }),
        ...(filters.position && { position: filters.position }),
        ...(filters.search && { search: filters.search })
      });

      const response = await fetch(`/backend/api/job_applications.php?path=applications&${params}`, { credentials: 'include' });
      const data = await response.json();
      
      if (data.success) {
        setApplications(data.applications || []);
        setPagination(data.pagination || {});
      }
    } catch (error) {
      console.error('Greška pri učitavanju prijava:', error);
    } finally {
      setLoading(false);
    }
  };

  const fetchPositions = async () => {
    try {
      const response = await fetch('/backend/api/job_applications.php?path=all_positions', { credentials: 'include' });
      // handle non-JSON / blocked responses gracefully
      const text = await response.text();
      let data;
      try {
        data = JSON.parse(text);
      } catch (e) {
        console.warn('Positions endpoint returned non-JSON. Falling back to positions derived from applications. Response preview:', text.slice(0, 200));
        data = null;
      }

      if (data && data.success) {
        setPositions(data.positions || []);
      } else {
        // Fallback: derive unique positions from already fetched applications
        const derived = Array.from(new Set(applications.map(a => a.position))).map(key => ({
          position_key: key,
          title: key,
          subtitle: '',
          enabled: true
        }));
        setPositions(derived);
      }
    } catch (error) {
      console.error('Greška pri učitavanju pozicija:', error);
      // Final fallback: derive from applications
      const derived = Array.from(new Set(applications.map(a => a.position))).map(key => ({
        position_key: key,
        title: key,
        subtitle: '',
        enabled: true
      }));
      setPositions(derived);
    }
  };

  const fetchStats = async () => {
    try {
      const response = await fetch('/backend/api/job_applications.php?path=stats');
      const data = await response.json();
      
      if (data.success && data.stats) {
        setStats({
          total: data.stats.total || 0,
          pending: data.stats.pending || 0,
          approved: data.stats.approved || 0,
          completed: data.stats.completed || 0,
          rejected: data.stats.rejected || 0
        });
      }
    } catch (error) {
      console.error('Greška pri učitavanju statistika:', error);
    }
  };

  const updateApplicationStatus = async (applicationId, newStatus) => {
    try {
      const response = await fetch('/backend/api/job_applications.php?path=application/status', {
        method: 'PUT',
        headers: {
          'Content-Type': 'application/json',
        },
        body: JSON.stringify({ id: applicationId, status: newStatus })
      });

      const data = await response.json();
      
      if (data.success) {
        fetchApplications();
        fetchStats();
        if (selectedApplication?.id === applicationId) {
          setSelectedApplication(prev => ({ ...prev, status: newStatus }));
        }
      } else {
        alert('Greška pri ažuriranju statusa: ' + (data.error || 'Nepoznata greška'));
      }
    } catch (error) {
      console.error('Greška:', error);
      alert('Greška pri ažuriranju statusa');
    }
  };

  const deleteApplication = async (applicationId) => {
    if (!confirm('Da li ste sigurni da želite da obrišete ovu prijavu?')) {
      return;
    }

    try {
      const response = await fetch(`/backend/api/job_applications.php?path=application&id=${applicationId}`, {
        method: 'DELETE'
      });

      const data = await response.json();
      
      if (data.success) {
        fetchApplications();
        fetchStats();
        setShowModal(false);
      } else {
        alert('Greška pri brisanju: ' + (data.error || 'Nepoznata greška'));
      }
    } catch (error) {
      console.error('Greška:', error);
      alert('Greška pri brisanju prijave');
    }
  };

  const getStatusBadge = (status) => {
    const badges = {
      pending: { color: 'bg-yellow-100 text-yellow-800 border-yellow-200', icon: Clock, text: 'Na čekanju' },
      approved: { color: 'bg-green-100 text-green-800 border-green-200', icon: CheckCircle, text: 'Prihvaćeno' },
      rejected: { color: 'bg-red-100 text-red-800 border-red-200', icon: XCircle, text: 'Odbačeno' },
      completed: { color: 'bg-blue-100 text-blue-800 border-blue-200', icon: Flag, text: 'Završeno' }
    };
    
    const badge = badges[status] || badges.pending;
    const Icon = badge.icon;
    
    return (
      <span className={`inline-flex items-center gap-1 px-2 py-1 rounded-full text-xs font-medium border ${badge.color}`}>
        <Icon className="w-3 h-3" />
        {badge.text}
      </span>
    );
  };

  const formatDate = (dateString) => {
    return new Date(dateString).toLocaleDateString('sr-RS', {
      year: 'numeric',
      month: 'short',
      day: 'numeric',
      hour: '2-digit',
      minute: '2-digit'
    });
  };

  const getPositionTitle = (positionKey) => {
    const position = positions.find(p => p.position_key === positionKey);
    return position ? position.title : positionKey;
  };

  const filteredApplications = applications.filter(app => {
    if (filters.search) {
      const searchLower = filters.search.toLowerCase();
      return (
        app.first_name.toLowerCase().includes(searchLower) ||
        app.last_name.toLowerCase().includes(searchLower) ||
        app.email.toLowerCase().includes(searchLower) ||
        app.phone.toLowerCase().includes(searchLower)
      );
    }
    return true;
  });

  return (
    <div className="space-y-6">
      {/* Stats */}
      <div className="grid grid-cols-1 md:grid-cols-5 gap-4">
        <div className="bg-background border border-border rounded-lg p-4">
          <div className="flex items-center">
            <div className="w-8 h-8 bg-blue-100 rounded-lg flex items-center justify-center">
              <FileText className="w-4 h-4 text-blue-600" />
            </div>
            <div className="ml-3">
              <p className="text-sm font-medium text-muted-foreground">Ukupno</p>
              <p className="text-2xl font-bold text-foreground">{stats.total || 0}</p>
            </div>
          </div>
        </div>
        
        <div className="bg-background border border-border rounded-lg p-4">
          <div className="flex items-center">
            <div className="w-8 h-8 bg-yellow-100 rounded-lg flex items-center justify-center">
              <Clock className="w-4 h-4 text-yellow-600" />
            </div>
            <div className="ml-3">
              <p className="text-sm font-medium text-muted-foreground">Na čekanju</p>
              <p className="text-2xl font-bold text-foreground">{stats.pending || 0}</p>
            </div>
          </div>
        </div>
        
        <div className="bg-background border border-border rounded-lg p-4">
          <div className="flex items-center">
            <div className="w-8 h-8 bg-green-100 rounded-lg flex items-center justify-center">
              <CheckCircle className="w-4 h-4 text-green-600" />
            </div>
            <div className="ml-3">
              <p className="text-sm font-medium text-muted-foreground">Prihvaćeno</p>
              <p className="text-2xl font-bold text-foreground">{stats.approved || 0}</p>
            </div>
          </div>
        </div>
        
        <div className="bg-background border border-border rounded-lg p-4">
          <div className="flex items-center">
            <div className="w-8 h-8 bg-blue-100 rounded-lg flex items-center justify-center">
              <Flag className="w-4 h-4 text-blue-600" />
            </div>
            <div className="ml-3">
              <p className="text-sm font-medium text-muted-foreground">Završeno</p>
              <p className="text-2xl font-bold text-foreground">{stats.completed || 0}</p>
            </div>
          </div>
        </div>
        
        <div className="bg-background border border-border rounded-lg p-4">
          <div className="flex items-center">
            <div className="w-8 h-8 bg-red-100 rounded-lg flex items-center justify-center">
              <XCircle className="w-4 h-4 text-red-600" />
            </div>
            <div className="ml-3">
              <p className="text-sm font-medium text-muted-foreground">Odbačeno</p>
              <p className="text-2xl font-bold text-foreground">{stats.rejected || 0}</p>
            </div>
          </div>
        </div>
      </div>

      {/* Filters */}
      <div className="bg-background border border-border rounded-lg p-4">
        <div className="flex flex-wrap items-center gap-4">
          <div className="flex items-center gap-2">
            <Search className="w-4 h-4 text-muted-foreground" />
            <input
              type="text"
              placeholder="Pretraga..."
              value={filters.search}
              onChange={(e) => setFilters(prev => ({ ...prev, search: e.target.value }))}
              className="px-3 py-2 border border-border rounded-md bg-background text-foreground focus:ring-2 focus:ring-primary focus:border-transparent"
            />
          </div>
          
          <select
            value={filters.status}
            onChange={(e) => setFilters(prev => ({ ...prev, status: e.target.value }))}
            className="px-3 py-2 border border-border rounded-md bg-background text-foreground focus:ring-2 focus:ring-primary focus:border-transparent"
          >
            <option value="">Svi statusi</option>
            <option value="pending">Na čekanju</option>
            <option value="approved">Prihvaćeno</option>
            <option value="rejected">Odbačeno</option>
            <option value="completed">Završeno</option>
          </select>
          
          <select
            value={filters.position}
            onChange={(e) => setFilters(prev => ({ ...prev, position: e.target.value }))}
            className="px-3 py-2 border border-border rounded-md bg-background text-foreground focus:ring-2 focus:ring-primary focus:border-transparent"
          >
            <option value="">Sve pozicije</option>
            {positions.map((position) => (
              <option key={position.position_key} value={position.position_key}>
                {position.title}
              </option>
            ))}
          </select>
          
          <button
            onClick={() => {
              setFilters({ status: '', position: '', search: '' });
              setPage(1);
            }}
            className="flex items-center gap-2 px-3 py-2 text-muted-foreground hover:text-foreground transition-colors"
          >
            <RefreshCw className="w-4 h-4" />
            Resetuj
          </button>
        </div>
      </div>

      {/* Applications Table */}
      <div className="bg-background border border-border rounded-lg overflow-hidden">
        {loading ? (
          <div className="text-center py-12">
            <RefreshCw className="w-8 h-8 animate-spin text-muted-foreground mx-auto mb-4" />
            <p className="text-muted-foreground">Učitavanje prijava...</p>
          </div>
        ) : filteredApplications.length === 0 ? (
          <div className="text-center py-12">
            <FileText className="w-16 h-16 text-muted-foreground mx-auto mb-4" />
            <h3 className="text-lg font-semibold text-foreground mb-2">Nema prijava</h3>
            <p className="text-muted-foreground">Nema prijava koje odgovaraju vašim filterima.</p>
          </div>
        ) : (
          <div className="overflow-x-auto">
            <table className="w-full">
              <thead className="bg-muted">
                <tr>
                  <th className="px-6 py-3 text-left text-xs font-medium text-muted-foreground uppercase tracking-wider">
                    Kandidat
                  </th>
                  <th className="px-6 py-3 text-left text-xs font-medium text-muted-foreground uppercase tracking-wider">
                    Pozicija
                  </th>
                  <th className="px-6 py-3 text-left text-xs font-medium text-muted-foreground uppercase tracking-wider">
                    Status
                  </th>
                  <th className="px-6 py-3 text-left text-xs font-medium text-muted-foreground uppercase tracking-wider">
                    Datum prijave
                  </th>
                  <th className="px-6 py-3 text-left text-xs font-medium text-muted-foreground uppercase tracking-wider">
                    Akcije
                  </th>
                </tr>
              </thead>
              <tbody className="divide-y divide-border">
                {filteredApplications.map((application) => (
                  <tr key={application.id} className="hover:bg-muted/50 transition-colors">
                    <td className="px-6 py-4">
                      <div>
                        <div className="text-sm font-medium text-foreground">
                          {application.first_name} {application.last_name}
                        </div>
                        <div className="flex items-center gap-4 text-sm text-muted-foreground mt-1">
                          <span className="flex items-center gap-1">
                            <Mail className="w-3 h-3" />
                            {application.email}
                          </span>
                          <span className="flex items-center gap-1">
                            <Phone className="w-3 h-3" />
                            {application.phone}
                          </span>
                        </div>
                      </div>
                    </td>
                    <td className="px-6 py-4">
                      <div className="flex items-center gap-2">
                        <Briefcase className="w-4 h-4 text-muted-foreground" />
                        <span className="text-sm text-foreground">
                          {getPositionTitle(application.position)}
                        </span>
                      </div>
                    </td>
                    <td className="px-6 py-4">
                      {getStatusBadge(application.status)}
                    </td>
                    <td className="px-6 py-4 text-sm text-muted-foreground">
                      <div className="flex items-center gap-1">
                        <Calendar className="w-3 h-3" />
                        {formatDate(application.created_at)}
                      </div>
                    </td>
                    <td className="px-6 py-4">
                      <div className="flex items-center gap-2">
                        <button
                          onClick={() => {
                            setSelectedApplication(application);
                            setShowModal(true);
                          }}
                          className="p-2 text-blue-600 hover:bg-blue-50 rounded-lg transition-colors"
                          title="Pogledaj detalje"
                        >
                          <Eye className="w-4 h-4" />
                        </button>
                        
                        {application.status === 'pending' && (
                          <>
                            <button
                              onClick={() => updateApplicationStatus(application.id, 'approved')}
                              className="p-2 text-green-600 hover:bg-green-50 rounded-lg transition-colors"
                              title="Prihvati"
                            >
                              <CheckCircle className="w-4 h-4" />
                            </button>
                            <button
                              onClick={() => updateApplicationStatus(application.id, 'rejected')}
                              className="p-2 text-red-600 hover:bg-red-50 rounded-lg transition-colors"
                              title="Odbaci"
                            >
                              <XCircle className="w-4 h-4" />
                            </button>
                          </>
                        )}
                        
                        {application.status === 'approved' && (
                          <button
                            onClick={() => updateApplicationStatus(application.id, 'completed')}
                            className="p-2 text-blue-600 hover:bg-blue-50 rounded-lg transition-colors"
                            title="Završi"
                          >
                            <Flag className="w-4 h-4" />
                          </button>
                        )}
                        
                        {application.cv_filename && (
                          <a
                            href={`/backend/api/job_applications.php?path=download&id=${application.id}`}
                            target="_blank"
                            rel="noopener noreferrer"
                            className="p-2 text-purple-600 hover:bg-purple-50 rounded-lg transition-colors"
                            title="Preuzmi CV"
                          >
                            <Download className="w-4 h-4" />
                          </a>
                        )}
                      </div>
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        )}
        
        {/* Pagination */}
        {pagination.pages > 1 && (
          <div className="px-6 py-4 border-t border-border">
            <div className="flex items-center justify-between">
              <div className="text-sm text-muted-foreground">
                Prikazano {((pagination.page - 1) * pagination.limit) + 1} - {Math.min(pagination.page * pagination.limit, pagination.total)} od {pagination.total} prijava
              </div>
              <div className="flex items-center gap-2">
                <button
                  onClick={() => setPage(prev => Math.max(1, prev - 1))}
                  disabled={pagination.page <= 1}
                  className="px-3 py-1 border border-border rounded-md text-sm disabled:opacity-50 disabled:cursor-not-allowed hover:bg-muted transition-colors"
                >
                  Prethodna
                </button>
                <span className="px-3 py-1 text-sm text-muted-foreground">
                  {pagination.page} / {pagination.pages}
                </span>
                <button
                  onClick={() => setPage(prev => Math.min(pagination.pages, prev + 1))}
                  disabled={pagination.page >= pagination.pages}
                  className="px-3 py-1 border border-border rounded-md text-sm disabled:opacity-50 disabled:cursor-not-allowed hover:bg-muted transition-colors"
                >
                  Sledeća
                </button>
              </div>
            </div>
          </div>
        )}
      </div>

      {/* Application Detail Modal */}
      {showModal && selectedApplication && (
        <div className="fixed inset-0 bg-black/50 backdrop-blur-sm z-50 flex items-center justify-center p-4">
          <div className="bg-card border border-border rounded-xl p-6 max-w-3xl w-full max-h-[90vh] overflow-y-auto">
            <div className="flex items-center justify-between mb-6">
              <h3 className="text-xl font-bold text-foreground">Detalji prijave</h3>
              <button
                onClick={() => setShowModal(false)}
                className="w-8 h-8 flex items-center justify-center rounded-lg hover:bg-muted transition-colors"
              >
                ×
              </button>
            </div>
            
            <div className="space-y-6">
              {/* Basic Info */}
              <div className="grid grid-cols-2 gap-4">
                <div>
                  <label className="block text-sm font-medium text-muted-foreground mb-1">Ime i prezime</label>
                  <p className="text-foreground flex items-center gap-2">
                    <User className="w-4 h-4" />
                    {selectedApplication.first_name} {selectedApplication.last_name}
                  </p>
                </div>
                <div>
                  <label className="block text-sm font-medium text-muted-foreground mb-1">Status</label>
                  {getStatusBadge(selectedApplication.status)}
                </div>
              </div>
              
              <div className="grid grid-cols-2 gap-4">
                <div>
                  <label className="block text-sm font-medium text-muted-foreground mb-1">Email</label>
                  <p className="text-foreground flex items-center gap-2">
                    <Mail className="w-4 h-4" />
                    {selectedApplication.email}
                  </p>
                </div>
                <div>
                  <label className="block text-sm font-medium text-muted-foreground mb-1">Telefon</label>
                  <p className="text-foreground flex items-center gap-2">
                    <Phone className="w-4 h-4" />
                    {selectedApplication.phone}
                  </p>
                </div>
              </div>
              
              <div className="grid grid-cols-2 gap-4">
                <div>
                  <label className="block text-sm font-medium text-muted-foreground mb-1">Pozicija</label>
                  <p className="text-foreground flex items-center gap-2">
                    <Briefcase className="w-4 h-4" />
                    {getPositionTitle(selectedApplication.position)}
                  </p>
                </div>
                <div>
                  <label className="block text-sm font-medium text-muted-foreground mb-1">Datum prijave</label>
                  <p className="text-foreground flex items-center gap-2">
                    <Calendar className="w-4 h-4" />
                    {formatDate(selectedApplication.created_at)}
                  </p>
                </div>
              </div>
              
              {selectedApplication.message && (
                <div>
                  <label className="block text-sm font-medium text-muted-foreground mb-2">Poruka</label>
                  <p className="text-foreground bg-muted p-4 rounded-lg whitespace-pre-wrap">
                    {selectedApplication.message}
                  </p>
                </div>
              )}
              
              {selectedApplication.cv_filename && (
                <div>
                  <label className="block text-sm font-medium text-muted-foreground mb-2">CV</label>
                  <a
                    href={`/backend/api/job_applications.php?path=download&id=${selectedApplication.id}`}
                    target="_blank"
                    rel="noopener noreferrer"
                    className="inline-flex items-center gap-2 px-4 py-2 bg-primary/10 text-primary border border-primary/20 rounded-lg hover:bg-primary/20 transition-colors"
                  >
                    <Download className="w-4 h-4" />
                    Preuzmi CV ({selectedApplication.cv_original_name || 'CV'})
                  </a>
                </div>
              )}
              
              {/* Actions */}
              <div className="flex gap-2 pt-4 border-t border-border">
                {selectedApplication.status === 'pending' && (
                  <>
                    <button
                      onClick={() => updateApplicationStatus(selectedApplication.id, 'approved')}
                      className="flex items-center gap-2 px-4 py-2 bg-green-500 hover:bg-green-600 text-white rounded-lg transition-colors"
                    >
                      <CheckCircle className="w-4 h-4" />
                      Prihvati
                    </button>
                    <button
                      onClick={() => updateApplicationStatus(selectedApplication.id, 'rejected')}
                      className="flex items-center gap-2 px-4 py-2 bg-red-500 hover:bg-red-600 text-white rounded-lg transition-colors"
                    >
                      <XCircle className="w-4 h-4" />
                      Odbaci
                    </button>
                  </>
                )}
                
                {selectedApplication.status === 'approved' && (
                  <button
                    onClick={() => updateApplicationStatus(selectedApplication.id, 'completed')}
                    className="flex items-center gap-2 px-4 py-2 bg-blue-500 hover:bg-blue-600 text-white rounded-lg transition-colors"
                  >
                    <Flag className="w-4 h-4" />
                    Završi proces
                  </button>
                )}
                
                <button
                  onClick={() => deleteApplication(selectedApplication.id)}
                  className="flex items-center gap-2 px-4 py-2 bg-gray-500 hover:bg-gray-600 text-white rounded-lg transition-colors ml-auto"
                >
                  <XCircle className="w-4 h-4" />
                  Obriši prijavu
                </button>
              </div>
            </div>
          </div>
        </div>
      )}
    </div>
  );
};

export default JobApplicationsManagement;